import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Text,
} from '@chakra-ui/react';
import { IconCheckmark2Small } from 'central-icons/IconCheckmark2Small';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { useEffect, useState } from 'react';

import { CenterLoading } from '@revelio/core';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import { useChartTheme } from '@revelio/replots';
import {
  Badge,
  BadgeColor,
  NavigationButton,
  RailModalBody,
  RailModalCloseButton,
  RailModalContent,
  RailModalDivider,
  RailModalFooter,
  RailModalHeader,
  RailModalPane,
  RailModalRail,
  RailModalRailItem,
  Button as UiButton,
} from '@revelio/ui';

import { DataProviderQueryStatus } from '../../next/CustomDash/components/DataProvider/types';
import { useCurrentRoleTaxonomyVersion } from '../selection-lists/hooks/useRoleTaxonomyAdapter';
import { useCurrentSkillTaxonomyVersion } from '../selection-lists/hooks/useSkillTaxonomyAdapter';
import { usePreferenceToast } from './hooks/usePreferenceToast';
import type {
  OnPreferenceUpdateCallback,
  preferenceEditors,
} from './preference-editors/preferenceEditors';
import styles from './preferences-modal.module.css';
import {
  PAGE_ORDER_SCOPES,
  PreferenceScope,
  SELF_MANAGED_PREFERENCE_KEYS,
  UserPreferenceKey,
  type UserPreferences,
} from './types';
import { UserPreferencesDataProvider } from './UserPreferencesDataProvider';
import { calculateVisiblePreferenceDiff } from './utils/preference-diff';
import {
  restoreVisiblePreferencesToDefaults,
  saveVisiblePreferencesAsDefault,
} from './utils/preference-operations';
import {
  getVisiblePreferenceEditors,
  getVisiblePreferenceKeys,
} from './utils/visibility';

/** Convenience types derived from registry */
type PreferenceEntry = (typeof preferenceEditors)[number];
type PreferenceId = PreferenceEntry['meta']['id'];

interface PreferencesModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  preferencesFromFilters?: Partial<UserPreferences>;
  onPreferenceUpdate: OnPreferenceUpdateCallback;
  affectedScope: PreferenceScope;
  preferenceVisibility?: (key: UserPreferenceKey) => boolean;
}

export const PreferencesModal = ({
  affectedScope,
  onPreferenceUpdate,
  preferencesFromFilters,
  preferenceVisibility,
  ...modalProps
}: PreferencesModalProps) => {
  const [activeId, setActiveId] = useState<PreferenceId>();
  const [isSaving, setIsSaving] = useState(false);
  const { isNewTheme } = useChartTheme();
  const showToast = usePreferenceToast();
  const { getSpecificTaxonomyVersion: getRoleTaxonomyVersion } =
    useCurrentRoleTaxonomyVersion();
  const { version: currentSkillTaxonomyVersion } =
    useCurrentSkillTaxonomyVersion();

  const effectivePreferenceVisibility = (key: UserPreferenceKey): boolean => {
    if (
      affectedScope === PreferenceScope.INDIVIDUAL &&
      key === UserPreferenceKey.DefaultFilters
    ) {
      return false;
    }
    if (
      key === UserPreferenceKey.PageOrder &&
      !PAGE_ORDER_SCOPES.includes(affectedScope)
    ) {
      return false;
    }
    return preferenceVisibility ? preferenceVisibility(key) : true;
  };

  const relevantEditors = getVisiblePreferenceEditors(
    effectivePreferenceVisibility
  );
  const visiblePreferenceKeys = getVisiblePreferenceKeys(
    effectivePreferenceVisibility
  );
  const activeEntry =
    relevantEditors.find((e) => e.meta.id === activeId) ?? relevantEditors[0];
  const ActiveEditorComponent = activeEntry?.component;

  useEffect(() => {
    setActiveId(activeEntry?.meta.id ?? undefined);
  }, [activeEntry]);

  // Handle setting current preferences as default
  const handleSetAsDefault = async (
    effectivePreferences: UserPreferences,
    refetchSavedPreferences: () => Promise<void>
  ) => {
    try {
      setIsSaving(true);
      // Resolve the version of the role taxonomy the saved defaults belong to.
      // Adaptive resolves through the effective selection it produced.
      const roleSelection = effectivePreferences.taxonomies?.role;
      const effectiveRoleSelection: CustomRoleTaxonomySelection =
        roleSelection === 'adaptive'
          ? (effectivePreferences.taxonomies
              ?.effectiveRoleTaxonomySelection ?? {
              default: true,
            })
          : (roleSelection ?? { default: true });
      await saveVisiblePreferencesAsDefault(
        affectedScope,
        effectivePreferences,
        visiblePreferenceKeys,
        refetchSavedPreferences,
        {
          role: getRoleTaxonomyVersion(effectiveRoleSelection),
          skill: currentSkillTaxonomyVersion,
        }
      );
      showToast('Preferences saved', 'success', {
        description: 'Your current preferences have been set as default.',
      });
    } catch (err) {
      showToast('Failed to save preferences', 'error', { error: err });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle resetting to defaults
  const handleRestoreDefaults = async (savedDefaults: UserPreferences) => {
    try {
      const { appliedDefaults } = restoreVisiblePreferencesToDefaults(
        affectedScope,
        savedDefaults,
        visiblePreferenceKeys
      );

      // Update local filters to reflect restored defaults for visible preferences only.
      // Self-managed prefs (e.g. PageOrder) handle their own reset and bypass this flow.
      visiblePreferenceKeys.forEach((key) => {
        if (SELF_MANAGED_PREFERENCE_KEYS.includes(key)) return;
        const value = appliedDefaults[key];
        onPreferenceUpdate(key as UserPreferenceKey, value);
      });

      showToast('Preferences restored', 'success', {
        description: 'Your preferences have been restored to defaults.',
      });
    } catch (err) {
      showToast('Failed to reset preferences', 'error', { error: err });
    }
  };

  return (
    <UserPreferencesDataProvider
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
    >
      {({ data, status }) => {
        if (!data) return null;
        const { preferences, savedPreferences, refetchSavedPreferences } = data;
        if (!preferences || !savedPreferences) return null;
        const hasError = status === DataProviderQueryStatus.Error;
        const isFetching = status === DataProviderQueryStatus.Fetching;
        const diff = calculateVisiblePreferenceDiff(
          preferences,
          savedPreferences,
          visiblePreferenceKeys,
          hasError
        );

        // Themed footer actions are the @revelio/ui ghost text buttons (the
        // closest DS variant to the mock's plain text button); legacy keeps
        // the Chakra link buttons byte-identical.
        const setAsDefaultButton = isNewTheme ? (
          <UiButton
            data-testid="preferences-set-as-default"
            variant="ghost"
            size="sm"
            className={styles.footerAction}
            onClick={() =>
              handleSetAsDefault(preferences, refetchSavedPreferences)
            }
            loading={isSaving}
          >
            Set as default
          </UiButton>
        ) : (
          <Button
            data-testid="preferences-set-as-default"
            variant="link"
            color="lightBlue.350"
            onClick={() =>
              handleSetAsDefault(preferences, refetchSavedPreferences)
            }
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            Set as default
          </Button>
        );
        const restoreDefaultsButton = isNewTheme ? (
          <UiButton
            data-testid="preferences-restore-defaults"
            variant="ghost"
            size="sm"
            className={styles.footerAction}
            onClick={() => handleRestoreDefaults(savedPreferences)}
          >
            Reset
          </UiButton>
        ) : (
          <Button
            data-testid="preferences-restore-defaults"
            variant="link"
            color="lightBlue.350"
            onClick={() => handleRestoreDefaults(savedPreferences)}
          >
            Restore defaults
          </Button>
        );

        const statusContent = (() => {
          if (hasError) {
            return (
              <Flex alignItems="center" color="orange.500" flex="1">
                <Icon as={IconCircleInfo} boxSize={4} />
                <Text fontSize="sm" ml={2}>
                  We couldn't load your saved defaults. You can still adjust
                  preferences locally.
                </Text>
              </Flex>
            );
          }
          if (diff.length === 0) {
            return (
              <HStack spacing={1} alignItems="center" flex="1">
                <Icon
                  as={IconCheckmark2Small}
                  color="text.primary"
                  boxSize={4}
                />
                <Text fontSize="sm" fontWeight="normal">
                  Dashboard settings match your default settings
                </Text>
              </HStack>
            );
          }

          const effectiveDiff = diff.filter(
            (d) => d !== UserPreferenceKey.Taxonomies
          );
          // The mock footer has no leading dot and no trailing colon; both
          // stay in legacy only.
          const differenceSuffix = isNewTheme ? '' : ':';
          const differenceText =
            effectiveDiff.length === 1
              ? `1 setting differs from your account defaults${differenceSuffix}`
              : `${effectiveDiff.length} settings differ from your account defaults${differenceSuffix}`;
          return (
            <Flex
              alignItems="center"
              flex="1"
              justifyContent={isNewTheme ? 'space-between' : 'flex-start'}
            >
              <HStack minWidth={0}>
                {!isNewTheme && <Badge size="sm" />}
                <Text fontSize="sm" fontWeight="normal">
                  {differenceText}
                </Text>
              </HStack>

              <HStack spacing={isNewTheme ? 3 : 1} fontSize="sm" ml={2} mr={1}>
                {isNewTheme ? (
                  <>
                    {restoreDefaultsButton}
                    {setAsDefaultButton}
                  </>
                ) : (
                  <>
                    {setAsDefaultButton}
                    <Text color="gray.400">/</Text>
                    {restoreDefaultsButton}
                  </>
                )}
              </HStack>
            </Flex>
          );
        })();

        const activeEditor = ActiveEditorComponent && (
          <ActiveEditorComponent
            affectedScope={affectedScope}
            onPreferenceUpdate={onPreferenceUpdate}
            preferences={preferences}
            diff={diff}
          />
        );

        if (isNewTheme) {
          return (
            <Modal size="2xl" isCentered {...modalProps}>
              <ModalOverlay />
              <RailModalContent asChild>
                <ModalContent className={styles.content}>
                  <RailModalHeader asChild>
                    <ModalHeader>Preferences</ModalHeader>
                  </RailModalHeader>
                  <RailModalCloseButton asChild>
                    <ModalCloseButton />
                  </RailModalCloseButton>
                  <RailModalDivider />
                  <RailModalBody asChild>
                    <ModalBody className={styles.modalBody}>
                      {isFetching ? (
                        <div className={styles.loading}>
                          <CenterLoading />
                        </div>
                      ) : (
                        <>
                          <RailModalRail
                            aria-label="Preference categories"
                            data-testid="user-preferences-sidebar"
                          >
                            {relevantEditors.flatMap(({ meta }, idx) => {
                              const hasDifference =
                                !hasError && diff.includes(meta.id);
                              const isSelfManaged =
                                SELF_MANAGED_PREFERENCE_KEYS.includes(meta.id);
                              const prevMetaId =
                                relevantEditors[idx - 1]?.meta.id;
                              const prevIsSelfManaged =
                                prevMetaId !== undefined &&
                                SELF_MANAGED_PREFERENCE_KEYS.includes(
                                  prevMetaId
                                );
                              // First self-managed entry after any regular ones
                              // gets a divider — self-managed prefs persist on
                              // their own and stay out of the diff/bulk-restore.
                              const showDivider =
                                isSelfManaged && !prevIsSelfManaged && idx > 0;
                              const item = (
                                <RailModalRailItem
                                  key={meta.id}
                                  isActive={meta.id === activeId}
                                  onClick={() => setActiveId(meta.id)}
                                >
                                  {meta.label}
                                  {hasDifference && (
                                    <Badge
                                      className={styles.diffDot}
                                      size="sm"
                                    />
                                  )}
                                </RailModalRailItem>
                              );
                              return showDivider
                                ? [
                                    <RailModalDivider
                                      key={`divider-before-${meta.id}`}
                                      className={styles.railDivider}
                                    />,
                                    item,
                                  ]
                                : [item];
                            })}
                            {relevantEditors.length === 0 && (
                              <Text>No preferences available</Text>
                            )}
                          </RailModalRail>
                          <RailModalPane className={styles.editorPane}>
                            {activeEditor}
                          </RailModalPane>
                        </>
                      )}
                    </ModalBody>
                  </RailModalBody>
                  <RailModalDivider />
                  <RailModalFooter className={styles.footer}>
                    {statusContent}
                  </RailModalFooter>
                </ModalContent>
              </RailModalContent>
            </Modal>
          );
        }

        return (
          <Modal size="2xl" isCentered {...modalProps}>
            <ModalOverlay />
            <ModalContent className={styles.content}>
              <ModalHeader>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>
                  Preferences
                </Text>
                {statusContent}
              </ModalHeader>
              <ModalCloseButton />
              <Divider borderColor="var(--pref-border, var(--chakra-colors-gray-200))" />
              <ModalBody p={0}>
                {isFetching && (
                  <Box
                    height="280px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CenterLoading />
                  </Box>
                )}
                {!isFetching && (
                  <Flex align="stretch" minH={80}>
                    {/* Sidebar of categories */}
                    <Box
                      minW="270px"
                      p={4}
                      borderRight="1px"
                      borderColor="var(--pref-border, var(--chakra-colors-gray-200))"
                      data-testid="user-preferences-sidebar"
                    >
                      <Stack spacing={2}>
                        {relevantEditors.flatMap(({ meta }, idx) => {
                          const hasDifference =
                            !hasError && diff.includes(meta.id);
                          const isSelfManaged =
                            SELF_MANAGED_PREFERENCE_KEYS.includes(meta.id);
                          const prevMetaId = relevantEditors[idx - 1]?.meta.id;
                          const prevIsSelfManaged =
                            prevMetaId !== undefined &&
                            SELF_MANAGED_PREFERENCE_KEYS.includes(prevMetaId);
                          // First self-managed entry after any regular ones
                          // gets a divider — self-managed prefs persist on
                          // their own and stay out of the diff/bulk-restore.
                          const showDivider =
                            isSelfManaged && !prevIsSelfManaged && idx > 0;
                          const diffIndicator = hasDifference && (
                            <Badge
                              className={styles.navigationMenuDiffIndicator}
                              size="sm"
                              color={
                                meta.id === activeId
                                  ? BadgeColor.WHITE
                                  : BadgeColor.DEFAULT
                              }
                            />
                          );
                          const button = (
                            <NavigationButton
                              key={meta.id}
                              className={styles.navigationMenuItem}
                              isSelected={meta.id === activeId}
                              size="sm"
                              slot={diffIndicator}
                              onClick={() => setActiveId(meta.id)}
                            >
                              {meta.label}
                            </NavigationButton>
                          );
                          return showDivider
                            ? [
                                <Divider
                                  key={`divider-before-${meta.id}`}
                                  borderColor="var(--pref-border, var(--chakra-colors-gray-200))"
                                />,
                                button,
                              ]
                            : [button];
                        })}
                        {relevantEditors.length === 0 && (
                          <Text>No preferences available</Text>
                        )}
                      </Stack>
                    </Box>

                    {/* Active editor */}
                    <Box flex="1" p={4}>
                      {activeEditor}
                    </Box>
                  </Flex>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        );
      }}
    </UserPreferencesDataProvider>
  );
};
